import React from 'react';
import { SchematicShell, Line } from './SchematicPrimitives';

const BULB_SPACING_FRAC = 0.08;
const BULB_R_FRAC = 0.012;

export function DormFairyLightsSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SideView
        depth={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
        mirror={view === 'right'}
      />
    );
  }

  return (
    <FrontView
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

function FrontView({ width, height, strokeColor, strokeWidth }) {
  const droop = height * 0.7;
  const clipW = width * 0.02;
  const clipH = height * 0.12;
  const topY = height * 0.1;

  const pathPoints = [];
  const N = 40;
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    const x = t * width;
    const normalized = 2 * t - 1;
    const y = topY + droop * (1 - normalized * normalized);
    pathPoints.push(`${i === 0 ? 'M' : 'L'} ${x} ${y}`);
  }

  const bulbs = [];
  let step = width * BULB_SPACING_FRAC;
  const bulbR = Math.max(width, height) * BULB_R_FRAC;
  let x = step * 0.5;
  while (x < width) {
    const t = x / width;
    const normalized = 2 * t - 1;
    const y = topY + droop * (1 - normalized * normalized);
    bulbs.push({ cx: x, cy: y });
    x += step;
  }

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      <rect
        x={0}
        y={topY - clipH}
        width={clipW}
        height={clipH}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <rect
        x={width - clipW}
        y={topY - clipH}
        width={clipW}
        height={clipH}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />

      <path
        d={pathPoints.join(' ')}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth * 0.5}
        vectorEffect="non-scaling-stroke"
      />

      {bulbs.map((b, i) => (
        <circle
          key={i}
          cx={b.cx}
          cy={b.cy}
          r={bulbR}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth * 0.6}
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </SchematicShell>
  );
}

function SideView({ depth, height, strokeColor, strokeWidth, mirror }) {
  const clipW = depth * 0.3;
  const clipH = height * 0.12;
  const topY = height * 0.1;
  const bulbR = Math.max(depth, height) * 0.04;

  return (
    <SchematicShell
      width={depth}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      <g
        transform={mirror ? `scale(-1, 1) translate(${-depth}, 0)` : undefined}
      >
        <rect
          x={depth / 2 - clipW / 2}
          y={topY - clipH}
          width={clipW}
          height={clipH}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          vectorEffect="non-scaling-stroke"
        />
        <Line
          x1={depth / 2}
          y1={topY}
          x2={depth / 2}
          y2={topY + height * 0.3}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth * 0.5}
        />
        <circle
          cx={depth / 2}
          cy={topY + height * 0.35}
          r={bulbR}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth * 0.6}
          vectorEffect="non-scaling-stroke"
        />
      </g>
    </SchematicShell>
  );
}

DormFairyLightsSchematic.supportsSideViews = true;

export function DormFairyLightsCurtainSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <CurtainSideView
        depth={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
        mirror={view === 'right'}
      />
    );
  }

  return (
    <CurtainFrontView
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

function CurtainFrontView({ width, height, strokeColor, strokeWidth }) {
  const topY = height * 0.04;
  const clipW = width * 0.02;
  const clipH = height * 0.04;
  const strandCount = Math.max(2, Math.round(width / (height * 0.1)));
  const spacing = width / (strandCount + 1);
  const bulbR = Math.max(width, height) * BULB_R_FRAC;
  const bulbStep = height * 0.08;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      <rect
        x={0}
        y={topY - clipH}
        width={clipW}
        height={clipH}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <rect
        x={width - clipW}
        y={topY - clipH}
        width={clipW}
        height={clipH}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />

      <Line
        x1={0}
        y1={topY}
        x2={width}
        y2={topY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth * 0.6}
      />

      {Array.from({ length: strandCount }, (_, s) => {
        const sx = (s + 1) * spacing;
        const bulbs = [];
        let by = topY + bulbStep;
        while (by < height) {
          bulbs.push(by);
          by += bulbStep;
        }
        return (
          <g key={s}>
            <Line
              x1={sx}
              y1={topY}
              x2={sx}
              y2={height}
              strokeColor={strokeColor}
              strokeWidth={strokeWidth * 0.3}
            />
            {bulbs.map((y, i) => (
              <circle
                key={i}
                cx={sx}
                cy={y}
                r={bulbR}
                fill="none"
                stroke={strokeColor}
                strokeWidth={strokeWidth * 0.6}
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </g>
        );
      })}
    </SchematicShell>
  );
}

function CurtainSideView({ depth, height, strokeColor, strokeWidth, mirror }) {
  const topY = height * 0.04;
  const clipW = depth * 0.3;
  const clipH = height * 0.04;
  const bulbR = Math.max(depth, height) * 0.015;
  const bulbStep = height * 0.08;

  const bulbs = [];
  let by = topY + bulbStep;
  while (by < height) {
    bulbs.push(by);
    by += bulbStep;
  }

  return (
    <SchematicShell
      width={depth}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      <g
        transform={mirror ? `scale(-1, 1) translate(${-depth}, 0)` : undefined}
      >
        <rect
          x={depth / 2 - clipW / 2}
          y={topY - clipH}
          width={clipW}
          height={clipH}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          vectorEffect="non-scaling-stroke"
        />
        <Line
          x1={depth / 2}
          y1={topY}
          x2={depth / 2}
          y2={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth * 0.3}
        />
        {bulbs.map((y, i) => (
          <circle
            key={i}
            cx={depth / 2}
            cy={y}
            r={bulbR}
            fill="none"
            stroke={strokeColor}
            strokeWidth={strokeWidth * 0.6}
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </g>
    </SchematicShell>
  );
}

DormFairyLightsCurtainSchematic.supportsSideViews = true;
